import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export function Card({
  title,
  actions,
  children,
  tight,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  tight?: boolean;
  className?: string;
}) {
  return (
    <section className={`card${className ? ` ${className}` : ''}`}>
      {(title || actions) && (
        <header className="card-head">
          {typeof title === 'string' ? <h2>{title}</h2> : title}
          {actions && <div className="spacer" />}
          {actions}
        </header>
      )}
      <div className={`card-body${tight ? ' tight' : ''}`}>{children}</div>
    </section>
  );
}

export function Chip({
  kind,
  title,
  children,
}: {
  kind?: string;
  title?: string;
  children: ReactNode;
}) {
  return (
    <span className={`chip${kind ? ` ${kind}` : ''}`} title={title}>
      {children}
    </span>
  );
}

export function Spinner() {
  return <div className="spinner" role="status" aria-label="Loading" />;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
  ariaLabel?: string;
}) {
  return (
    <div className="segmented" role="group" aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={option.value === value ? 'active' : ''}
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

// --------------------------------------------------------------------------
// Toasts
// --------------------------------------------------------------------------

const ToastContext = createContext<(message: string) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<string | null>(null);

  const show = useCallback((next: string) => {
    setMessage(next);
    window.setTimeout(() => setMessage((current) => (current === next ? null : current)), 4000);
  }, []);

  const value = useMemo(() => show, [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {message && (
        <div className="toast" role="status" aria-live="polite">
          {message}
        </div>
      )}
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);

// --------------------------------------------------------------------------
// Dates
// --------------------------------------------------------------------------

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAYS_FR = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_FR = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

import type { SlotType } from '../types';

const SLOT_LABELS: Record<SlotType, { en: string; fr: string }> = {
  breakfast: { en: 'Breakfast', fr: 'Petit-déjeuner' },
  morning_snack: { en: 'Snack box', fr: 'Collation' },
  lunch: { en: 'Lunch', fr: 'Déjeuner' },
  gouter: { en: 'Goûter', fr: 'Goûter' },
  dinner: { en: 'Dinner', fr: 'Dîner' },
  dessert: { en: 'Dessert', fr: 'Dessert' },
  evening_drink: { en: 'Evening drink', fr: 'Apéritif' },
  aperitif_nibble: { en: 'Something with it', fr: 'À grignoter' },
};

/** Order the sittings appear in within a day. */
export const SLOT_ORDER: SlotType[] = [
  'breakfast',
  'morning_snack',
  'lunch',
  'gouter',
  'dinner',
  'dessert',
  'evening_drink',
  'aperitif_nibble',
];

export function slotLabel(slot: string, language: 'en' | 'fr' = 'en'): string {
  return SLOT_LABELS[slot as SlotType]?.[language] ?? slot;
}

export function bySlotOrder(a: { slotType: string }, b: { slotType: string }): number {
  return (
    SLOT_ORDER.indexOf(a.slotType as SlotType) - SLOT_ORDER.indexOf(b.slotType as SlotType)
  );
}

export function dayName(date: string, language: 'en' | 'fr' = 'en'): string {
  const index = (new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7;
  return (language === 'fr' ? DAYS_FR : DAYS)[index] ?? '';
}

export function shortDate(date: string, language: 'en' | 'fr' = 'en'): string {
  const d = new Date(`${date}T00:00:00Z`);
  const month = (language === 'fr' ? MONTHS_FR : MONTHS)[d.getUTCMonth()] ?? '';
  return `${d.getUTCDate()} ${month}`;
}

export function relativeDay(date: string, today: string, language: 'en' | 'fr' = 'en'): string | null {
  const diff = Math.round((Date.parse(date) - Date.parse(today)) / 86_400_000);
  if (diff === 0) return language === 'fr' ? "Aujourd'hui" : 'Today';
  if (diff === 1) return language === 'fr' ? 'Demain' : 'Tomorrow';
  if (diff === -1) return language === 'fr' ? 'Hier' : 'Yesterday';
  return null;
}

export const todayIso = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
};

/**
 * The start of the week a date falls in.
 *
 * `startsOn` is a weekday index (Monday = 0) and is the day of the household's
 * main shop, because that is what actually bounds a week's food. It comes from
 * the server; Monday is only the fallback for a household that has not said
 * when it shops.
 */
export function startOfWeek(date: string, startsOn = 0): string {
  const d = new Date(`${date}T00:00:00Z`);
  const weekday = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - ((((weekday - startsOn) % 7) + 7) % 7));
  return d.toISOString().slice(0, 10);
}

export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
